import * as React from 'react';
import { Component } from 'react';
import { createPortal } from 'react-dom';
import moment, { Moment } from 'moment';
import { AppUser } from '../../views/App/App';
import { Report } from '../../models/report';
import { SchoolEvent } from '../../models/event';
import { Point } from '../../models/venue';
import { SimpleModal } from '../SimpleModal/SimpleModal';
import { Button } from '../Button/Button';
import { ConsentRequestTemplate } from '../ConsentRequestTemplate/ConsentRequestTemplate';
import { FormAnswersSummary } from '../FormAnswersSummary/FormAnswersSummary';
import { ConsentRequestHistory } from '../ConsentRequestHistory/ConsentRequestHistory';
import { answersToMap } from 'Src/helpers/form/formRuntime';
import { getRespondedAt, getResponseUserText, wasEditedAfterResponse } from 'Src/helpers/message/message';
import { FormElement } from '../../types/formSchema';
import { EventInvitationMessage, MessageField } from '../../models/message';
import { FormSubmission } from '../../types/formSchema';
import { updateStaffConsentStatus } from '../../helpers/service/admin/messages';
import { ParentalConsentRequestForm } from '../../views/GenericView/ParentView/ConsentRequests/ConsentRequestsInbox/ParentalConsentRequestForm/ParentalConsentRequestForm';
import './SchoolParentalConsentSummary.scss';

type Status = 'ACCEPTED' | 'DECLINED' | 'PENDING' | 'NOT_SENT' | 'NO_PARENT_LINKED';
type Filter = 'ALL' | Status;
type View = 'STATUS' | 'ANSWERS';

interface Props {
  consents: Report[];
  schoolEvent: SchoolEvent;
  user: AppUser;
  onViewMapClick: (point: Point) => void;
  showEditedPill?: boolean;
  onConsentUpdated: (consent: Report) => void;
}

interface State {
  statusFilter: Filter;
  selectedConsentIndex: number | null;
  studentSearch: string;
  view: View;
  editStatus: 'ACCEPTED' | 'REJECTED' | null;
  isSaving: boolean;
  saveError: string;
}

const STATUS_LABELS: Record<Status, string> = {
  ACCEPTED: 'Accepted', DECLINED: 'Declined', PENDING: 'Pending', NOT_SENT: 'Not sent', NO_PARENT_LINKED: 'No parent linked'
};
const STATUS_ORDER: Record<Status, number> = { DECLINED: 0, PENDING: 1, NOT_SENT: 2, ACCEPTED: 3, NO_PARENT_LINKED: 4 };

export class SchoolParentalConsentSummary extends Component<Props, State> {
  state: State = { statusFilter: 'ALL', selectedConsentIndex: null, studentSearch: '', view: 'STATUS', editStatus: null, isSaving: false, saveError: '' };

  closeDrawer = () => {
    if (!this.state.isSaving) this.setState({ selectedConsentIndex: null, editStatus: null, saveError: '' });
  };

  saveStatus = async (fields?: MessageField[], submission?: FormSubmission) => {
    const { selectedConsentIndex, editStatus, isSaving } = this.state;
    if (selectedConsentIndex === null || !editStatus || isSaving) return;
    const consent = this.props.consents[selectedConsentIndex];
    this.setState({ isSaving: true, saveError: '' });
    try {
      const updated = await updateStaffConsentStatus(this.props.user, this.props.schoolEvent.id, consent.id, {
        invitationStatus: editStatus, expectedStatus: consent.invitationStatus,
        ...(editStatus === 'ACCEPTED' ? { fields, ...(submission || {}) } : {})
      });
      this.props.onConsentUpdated(updated);
      this.setState({ editStatus: null, isSaving: false });
    } catch (error) {
      this.setState({ isSaving: false, saveError: error?.response?.data?.message || 'Unable to update consent. Refresh and try again.' });
    }
  };

  getParents(consent: any): any[] {
    const player = consent.playerDetailsData || {};
    const direct = player.parents || player.parentsData || player.parentDetails || [];
    if (Array.isArray(direct) && direct.length) return direct;
    const playerId = player.id || consent.playerDetails?.userId;
    const match: any = this.props.consents.find((item: any) =>
      (item.playerDetailsData?.id || item.playerDetails?.userId) === playerId && Array.isArray(item.playerDetailsData?.parents)
    );
    return match?.playerDetailsData?.parents || [];
  }

  getStatus(consent: any): Status {
    const raw = typeof consent.invitationStatus === 'object' ? consent.invitationStatus?.status : consent.invitationStatus;
    const status = String(raw || '').toUpperCase();
    if (status === 'NOT_SEND' || status === 'NOT_SENT' || !consent.id) return this.getParents(consent).length ? 'NOT_SENT' : 'NO_PARENT_LINKED';
    if (['ACCEPTED', 'BOOKED', 'GOT_IT'].includes(status)) return 'ACCEPTED';
    if (['REJECTED', 'DECLINED', 'REFUSED'].includes(status)) return 'DECLINED';
    return 'PENDING';
  }

  getName(consent: any): string {
    return [consent.playerDetailsData?.firstName, consent.playerDetailsData?.lastName].filter(Boolean).join(' ');
  }

  getParentName(parent: any): string {
    return typeof parent === 'string' ? parent : parent?.fullName || [parent?.firstName, parent?.lastName].filter(Boolean).join(' ') || parent?.email || 'Parent';
  }

  getDeadline(consent: any): { value: Moment | null; custom: boolean } {
    const raw = consent.deadlineForAnswers;
    const custom = raw !== null && raw !== undefined && raw !== '';
    const value = moment(custom ? raw : consent.eventData?.startTime || this.props.schoolEvent.startTime);
    return { value: value.isValid() ? value : null, custom };
  }

  getSentAt(consent: any): Moment | null {
    const sent = moment(consent.createdAt);
    return sent.isValid() ? sent : null;
  }

  getStudentMeta(consent: any): string {
    const player = consent.playerDetailsData || {};
    const text = (value: unknown): string =>
      typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
    const year = text(player.yearName) || text(player.year) || text(player.yearGroup) || text(player.form?.ageGroup);
    const form = text(player.formName) || text(player.registrationClass) || text(player.className) || text(player.form?.name) || text(player.form);
    const yearLabel = /^\d+$/.test(year) ? `Year ${year}` : year;
    return [yearLabel, form].filter(Boolean).join(' · ');
  }

  getQuestions(): FormElement[] {
    const source: any = this.props.consents.find((item: any) => item.formDefinition?.elements?.length);
    return (source?.formDefinition?.elements || []).filter((element: FormElement) => element.kind === 'QUESTION');
  }

  getAnswer(consent: any, id: string): React.ReactNode {
    if (!['ACCEPTED', 'DECLINED'].includes(this.getStatus(consent))) return '—';
    const answer: any = (consent.formAnswers || []).find(item => item.fieldId === id);
    if (!answer || answer.value === '' || answer.value === null || answer.value === undefined) return <span className="consentAnswer--empty">Not answered</span>;
    const value = Array.isArray(answer.value) ? answer.value.join(', ') : typeof answer.value === 'boolean' ? (answer.value ? 'Yes' : 'No') : String(answer.value);
    return answer.otherValue ? `${value} — ${answer.otherValue}` : value;
  }

  getVisibleRows() {
    const query = this.state.studentSearch.trim().toLowerCase();
    return this.props.consents.map((consent, index) => ({ consent, index })).filter(({ consent }) =>
      (this.state.statusFilter === 'ALL' || this.getStatus(consent) === this.state.statusFilter) &&
      (!query || this.getName(consent).toLowerCase().includes(query))
    ).sort((a, b) => {
      const sa = this.getStatus(a.consent), sb = this.getStatus(b.consent);
      if (this.state.statusFilter === 'ALL' && STATUS_ORDER[sa] !== STATUS_ORDER[sb]) return STATUS_ORDER[sa] - STATUS_ORDER[sb];
      if (['PENDING', 'NOT_SENT'].includes(sa) && ['PENDING', 'NOT_SENT'].includes(sb)) {
        const da = this.getDeadline(a.consent).value?.valueOf() || Number.MAX_SAFE_INTEGER;
        const db = this.getDeadline(b.consent).value?.valueOf() || Number.MAX_SAFE_INTEGER;
        if (da !== db) return da - db;
      }
      return this.getName(a.consent).localeCompare(this.getName(b.consent));
    });
  }

  renderStatus(status: Status) {
    return <span className={`consentStatus consentStatus--${status.toLowerCase()}`}><i />{STATUS_LABELS[status]}</span>;
  }

  renderDeadline(consent: any) {
    const status = this.getStatus(consent);
    if (status === 'NO_PARENT_LINKED') return '—';
    const { value, custom } = this.getDeadline(consent);
    if (!value) return '—';
    if (!['PENDING', 'NOT_SENT'].includes(status)) return <span className="consentDeadline consentDeadline--muted">{value.format('D MMM, HH:mm')}</span>;
    const days = value.diff(moment(), 'days', true);
    if (days <= 0) return <span className="consentDeadline consentDeadline--overdue"><strong>Overdue</strong><small>Was due {value.format('D MMM, HH:mm')}</small></span>;
    const rounded = Math.ceil(days);
    if (rounded <= 3) return <span className="consentDeadline consentDeadline--soon"><strong>Due in {rounded} {rounded === 1 ? 'day' : 'days'}</strong><small>{value.format('D MMM, HH:mm')}</small></span>;
    return <span className="consentDeadline"><strong>{value.format('D MMM, HH:mm')}</strong><small>{custom ? 'Custom deadline' : 'Event start'} · in {rounded} days</small></span>;
  }

  renderResponse(consent: any) {
    const status = this.getStatus(consent);
    if (status === 'NO_PARENT_LINKED') return <span className="consentResponse__muted">Can’t receive the request</span>;
    if (status === 'NOT_SENT') return <span className="consentResponse__muted">Parent linked after request was sent</span>;
    if (status === 'PENDING') {
      const sent = this.getSentAt(consent);
      return <><strong>{sent ? `Sent ${sent.format('D MMM')}` : 'Request sent'}</strong><small>No reminder yet</small></>;
    }
    const responded = getRespondedAt(consent);
    const edited = wasEditedAfterResponse(consent);
    return <><strong>{getResponseUserText(consent) || 'Parent'}</strong><small>{responded ? moment(responded).format('D MMM, HH:mm') : 'Response received'}</small>{edited ? <em>Edited {moment(consent.updatedAt).format('D MMM, HH:mm')}</em> : null}</>;
  }

  renderTable() {
    const rows = this.getVisibleRows();
    const questions = this.getQuestions();
    const answers = this.state.view === 'ANSWERS';
    const columns = answers ? 3 + Math.max(questions.length, 1) : 6;
    return <>
      <div className="consentSummary__tableWrap"><table className="consentSummary__table">
        <thead><tr><th>Student</th><th>Status</th>{answers ? questions.length ? questions.map(q => <th key={q.id} title={q.title}>{q.title || 'Question'}</th>) : <th>Answers</th> : <><th>Parents</th><th>Response</th><th>Deadline</th><th aria-label="Actions" /></>}</tr></thead>
        <tbody>{rows.length ? rows.map(({ consent, index }) => {
          const status = this.getStatus(consent), parents = this.getParents(consent), meta = this.getStudentMeta(consent);
          return <tr key={consent.id || `consent_${index}`} className={`consentSummaryRow consentSummaryRow--${status.toLowerCase()}`} onClick={() => this.setState({ selectedConsentIndex: index })}>
            <td><strong>{this.getName(consent)}</strong>{meta ? <small>{meta}</small> : null}</td><td>{this.renderStatus(status)}{consent.updatedByStaff ? <small>Updated by staff</small> : null}</td>
            {answers ? questions.length ? questions.map(q => <td className="consentAnswer" key={q.id}>{this.getAnswer(consent, q.id)}</td>) : <td>—</td> : <>
              <td>{parents.length ? <>{this.getParentName(parents[0])}{parents.length > 1 ? <small>+{parents.length - 1}</small> : null}</> : '—'}</td>
              <td className="consentResponse">{this.renderResponse(consent)}</td><td>{this.renderDeadline(consent)}</td><td className="consentSummary__chevron">{status !== 'NO_PARENT_LINKED' ? '›' : ''}</td>
            </>}
          </tr>;
        }) : <tr><td className="consentSummary__empty" colSpan={columns}><strong>No students match</strong><span>Try a different filter or clear the search.</span></td></tr>}</tbody>
      </table></div>
      <footer className="consentSummary__footer"><span>{rows.length} of {this.props.consents.length} students</span><span>Sorted by: <strong>needs attention first</strong></span></footer>
    </>;
  }

  renderDrawer() {
    const index = this.state.selectedConsentIndex;
    if (index === null) return null;
    const consent: any = this.props.consents[index];
    if (!consent) return null;
    const status = this.getStatus(consent), name = this.getName(consent), parents = this.getParents(consent);
    const { value: deadline, custom } = this.getDeadline(consent);
    const overdue = ['PENDING', 'NOT_SENT'].includes(status) && !!deadline && moment().isSameOrAfter(deadline);
    const parentName = getResponseUserText(consent) || (consent.updatedByStaff ? 'A staff member' : (parents[0] && this.getParentName(parents[0])) || 'A parent');
    const statusTitle = overdue ? 'Overdue' : status === 'ACCEPTED' ? 'Consent given' : status === 'DECLINED' ? 'Consent declined' : status === 'NOT_SENT' ? 'Not sent yet' : status === 'NO_PARENT_LINKED' ? 'No parent linked' : 'Waiting for a parent';
    const statusText = overdue ? `The deadline passed on ${deadline?.format('D MMM, HH:mm')} with no response.` : status === 'ACCEPTED' ? `${parentName} accepted this request.` : status === 'DECLINED' ? `${parentName} declined this request. ${name} shouldn’t be selected for this event.` : status === 'NOT_SENT' ? `A parent was linked after the request went out.` : status === 'NO_PARENT_LINKED' ? `${name} has no linked parent, so the request can’t be sent.` : `The request has been sent and is awaiting a response.`;
    const initials = name.split(' ').map(part => part[0]).slice(0, 2).join('');
    const canEdit = ['ADMIN', 'MANAGER', 'TEACHER', 'COACH'].includes(this.props.user.activeRole)
      && consent.kind === 'EventInvitationMessage' && ['NOT_READY', 'ACCEPTED', 'REJECTED', 'OUTDATED'].includes(consent.invitationStatus);
    const drawer = <SimpleModal isOpen title="Consent details" onCloseClick={this.closeDrawer} onBackdropClick={this.closeDrawer} showFooter={false} customClass="schoolConsentDrawer" bodyClassName="schoolConsentDrawer__body" side="right">
      <div className="schoolConsentDrawer__content">
        <header className="schoolConsentDrawer__profile"><span className="schoolConsentDrawer__avatar">{initials}</span><div><h2>{name}</h2>{this.getStudentMeta(consent) ? <p>{this.getStudentMeta(consent)}</p> : null}{this.renderStatus(status)}</div></header>
        {consent.updatedByStaff ? <p>Updated by staff{consent.staffUpdatedAt ? ` · ${moment(consent.staffUpdatedAt).format('D MMM YYYY, HH:mm')}` : ''}</p> : null}
        {canEdit ? <section className="schoolConsentDrawer__section schoolConsentDrawer__statusActions"><h3>Change consent status</h3>
          {this.state.saveError ? <div className="alert alert-danger" role="alert">{this.state.saveError}</div> : null}
          {this.state.isSaving ? <p role="status">Saving…</p> : this.state.editStatus === 'ACCEPTED' ?
            <ParentalConsentRequestForm user={this.props.user} message={consent as unknown as EventInvitationMessage} staffMode
              submitLabel="Accept consent" onAcceptClick={this.saveStatus}
              onCloseClick={() => this.setState({ editStatus: null, saveError: '' })} /> :
            this.state.editStatus === 'REJECTED' ? <><p>Reject this consent request? It will be marked Updated by staff.</p>
              <div className="schoolConsentDrawer__actions">
                <Button text="Cancel" customClass="sit-btn sit-btn--secondary" onClick={() => this.setState({ editStatus: null })}/>
                <Button text="Reject consent" customClass="sit-btn sit-btn--danger" onClick={() => this.saveStatus()}/>
              </div></> : <div className="schoolConsentDrawer__actions">
              {status !== 'ACCEPTED' ? <Button text="Accept consent" customClass="sit-btn sit-btn--primary" onClick={() => this.setState({ editStatus: 'ACCEPTED', saveError: '' })}/> : null}
              {status !== 'DECLINED' ? <Button text="Reject consent" customClass="sit-btn sit-btn--danger" onClick={() => this.setState({ editStatus: 'REJECTED', saveError: '' })}/> : null}
            </div>}
        </section> : null}
        <section className={`schoolConsentDrawer__statusCard schoolConsentDrawer__statusCard--${overdue ? 'overdue' : status.toLowerCase()}`}><strong>{statusTitle}</strong><p>{statusText}</p>{status === 'DECLINED' && consent.declineReason ? <blockquote>“{consent.declineReason}”</blockquote> : null}</section>
        {status !== 'NO_PARENT_LINKED' && deadline ? <section className="schoolConsentDrawer__section"><h3>Deadline</h3><div className={overdue ? 'schoolConsentDrawer__deadlineValue is-overdue' : 'schoolConsentDrawer__deadlineValue'}><strong>{deadline.format('ddd D MMM YYYY, HH:mm')}</strong><span>{custom ? 'Custom deadline for this request' : 'Default — event start'}</span></div></section> : null}
        <section className="schoolConsentDrawer__section"><h3>Parents</h3>{parents.length ? parents.map((parent, parentIndex) => <div className="schoolConsentDrawer__parent" key={parent?.id || parentIndex}><span>{this.getParentName(parent).split(' ').map(x => x[0]).slice(0,2).join('')}</span><div><strong>{this.getParentName(parent)}</strong>{typeof parent !== 'string' && parent.email ? <small>{parent.email}</small> : null}</div>{getResponseUserText(consent) === this.getParentName(parent) ? <em>Responded</em> : null}</div>) : <p className="schoolConsentDrawer__empty">No linked parent</p>}</section>
        {status !== 'NO_PARENT_LINKED' ? <section className="schoolConsentDrawer__section"><h3>History</h3><ConsentRequestHistory message={consent}/></section> : null}
        {status !== 'NO_PARENT_LINKED' ? this.renderAnswers(consent, status) : null}
      </div>
    </SimpleModal>;
    return typeof document === 'undefined' ? drawer : createPortal(drawer, document.body);
  }

  renderAnswers(consent: any, status: Status) {
    const hasForm = consent.formDefinition || (Array.isArray(consent.fields) && consent.fields.length);
    return <section className="schoolConsentDrawer__section schoolConsentDrawer__answers"><h3>Answers</h3>{hasForm ? consent.formDefinition ? <FormAnswersSummary definition={consent.formDefinition} answers={answersToMap(consent.formAnswers || [])} /> : <ConsentRequestTemplate consent={consent}/> : <div className="schoolConsentDrawer__empty">{['ACCEPTED','DECLINED'].includes(status) ? 'No additional questions were included.' : 'Awaiting response'}</div>}</section>;
  }

  render() {
    const counts = this.props.consents.reduce((all, consent) => { const status = this.getStatus(consent); all[status]++; return all; }, { ACCEPTED: 0, DECLINED: 0, PENDING: 0, NOT_SENT: 0, NO_PARENT_LINKED: 0 });
    const reachable = this.props.consents.length - counts.NO_PARENT_LINKED, responded = counts.ACCEPTED + counts.DECLINED;
    const percent = reachable ? Math.round(responded / reachable * 100) : 0;
    const firstSent = this.props.consents.map(c => this.getSentAt(c)).filter(Boolean).sort((a,b) => a!.valueOf()-b!.valueOf())[0];
    const eventStart = moment(this.props.schoolEvent.startTime);
    const overdue = this.props.consents.filter(c => this.getStatus(c) === 'PENDING' && this.getDeadline(c).value?.isBefore(moment())).length;
    const filters: Array<{key: Filter; label: string; count: number}> = [{key:'ALL',label:'All',count:this.props.consents.length}, ...(['ACCEPTED','DECLINED','PENDING','NOT_SENT','NO_PARENT_LINKED'] as Status[]).map(key => ({key,label:STATUS_LABELS[key],count:counts[key]}))];
    return <section className="consentSummary">
      {this.renderDrawer()}
      <header className="consentSummary__header"><div><h2>Parental consent</h2><p>{firstSent ? <>Request sent <b>{firstSent.format('D MMM YYYY')}</b> to {reachable} students</> : <>Consent status for {reachable} reachable students</>}{eventStart.isValid() ? <> · Responses close <b>{eventStart.format('ddd D MMM, HH:mm')}</b> (event start)</> : null}{overdue ? <> · <strong className="is-overdue">{overdue} overdue</strong></> : null}</p></div></header>
      <div className="consentSummary__overview"><div className="consentSummary__score"><strong>{counts.ACCEPTED} <span>/ {reachable}</span></strong><p>accepted · {percent}% responded</p></div><div><div className="consentSummary__progress" aria-label={`${percent}% responded`}><i className="is-accepted" style={{flex:counts.ACCEPTED}}/><i className="is-declined" style={{flex:counts.DECLINED}}/><i className="is-pending" style={{flex:counts.PENDING}}/><i className="is-not-sent" style={{flex:counts.NOT_SENT}}/></div><div className="consentSummary__filters">{filters.map(filter => <button key={filter.key} className={this.state.statusFilter === filter.key ? 'is-active' : ''} onClick={() => this.setState({statusFilter:filter.key})}><i className={`is-${filter.key.toLowerCase()}`}/>{filter.label} <span>{filter.count}</span></button>)}</div></div></div>
      {counts.NO_PARENT_LINKED ? <aside className="consentSummary__warning"><i>!</i><p><strong>{counts.NO_PARENT_LINKED} students have no linked parent</strong> and can’t receive this request.</p><button onClick={() => this.setState({statusFilter:'NO_PARENT_LINKED'})}>Show students</button></aside> : null}
      <div className="consentSummary__toolbar"><div><span>Show</span><button className={this.state.view === 'STATUS' ? 'is-active' : ''} onClick={() => this.setState({view:'STATUS'})}>Status</button><button className={this.state.view === 'ANSWERS' ? 'is-active' : ''} onClick={() => this.setState({view:'ANSWERS'})}>Answers</button></div><label><span className="consentSummary__searchIcon"/><input type="search" value={this.state.studentSearch} placeholder="Search students" onChange={e => this.setState({studentSearch:e.target.value})}/>{this.state.studentSearch ? <button aria-label="Clear search" onClick={() => this.setState({studentSearch:''})}>×</button> : null}</label></div>
      {this.renderTable()}
    </section>;
  }
}
